import React, { useState } from "react";
import { motion } from "framer-motion";
import { Facebook, Menu, X, Info, MessageCircle, Recycle, Wrench } from "lucide-react";
import { BIZ, SERVICES, PRODUCTS, STEPS } from "./jehannahData";
import ConceptCTAPill from "@/components/ConceptCTAPill";

const COPPER = "#B5651D";
const CHARCOAL = "#1C1815";
const CREAM = "#F7F2E9";

const fadeUp = { initial: { opacity: 0, y: 22 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-60px" }, transition: { duration: 0.55, ease: "easeOut" } };

export default function JehannahSite() {
  const [open, setOpen] = useState(false);
  const links = [["Custom Work", "#custom"], ["Specs", "#specs"], ["Repairs", "#repairs"], ["Visit", "#visit"]];

  return (
    <div id="top" className="min-h-screen" style={{ background: CREAM, color: "#241C14", fontFamily: "'IBM Plex Mono', monospace" }} data-testid="jehannah-concept-root">
      <style>{`@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500;600;700&family=Work+Sans:wght@400;500;600;700&display=swap'); .jh-sans{font-family:'Work Sans',sans-serif;}`}</style>

      <div data-testid="jewelry-disclaimer" className="bg-black text-white text-center text-xs jh-sans py-2.5">
        <span className="inline-flex items-center gap-2"><Info size={13} style={{ color: COPPER }} />Concept website created by <a href="https://mysitecare.eu/ph" target="_blank" rel="noopener noreferrer" className="underline decoration-white/40 hover:decoration-white">MySiteCare</a> — not currently operated by or affiliated with {BIZ.name}.</span>
      </div>

      <header className="sticky top-0 z-40" style={{ background: CHARCOAL }}>
        <nav className="max-w-6xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
          <a href="#top" data-testid="jehannah-logo" className="text-lg font-bold" style={{ color: CREAM }}>JEHANNAH GOLDSMITH</a>
          <div className="hidden md:flex items-center gap-8 jh-sans">
            {links.map(([l, h]) => <a key={h} href={h} className="text-sm" style={{ color: "#C7B79E" }}>{l}</a>)}
          </div>
          <a href={BIZ.facebook} target="_blank" rel="noopener noreferrer" data-testid="jehannah-header-cta" className="hidden sm:inline-flex jh-sans px-6 py-2.5 text-sm font-semibold" style={{ background: COPPER, color: CHARCOAL }}>Message Us</a>
          <button className="md:hidden" style={{ color: CREAM }} onClick={() => setOpen(!open)} data-testid="jehannah-menu-toggle" aria-label="Menu">{open ? <X size={24} /> : <Menu size={24} />}</button>
        </nav>
        {open && <div className="md:hidden px-5 pb-4 space-y-1 jh-sans" style={{ background: CHARCOAL }}>{links.map(([l, h]) => <a key={h} href={h} onClick={() => setOpen(false)} className="block py-2.5" style={{ color: "#C7B79E" }}>{l}</a>)}</div>}
      </header>

      {/* HERO — no photo asset available: technical spec-sheet motif carries the identity */}
      <section className="relative py-28 sm:py-32 text-center overflow-hidden" style={{ background: CHARCOAL }} data-testid="jehannah-hero">
        <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "linear-gradient(rgba(181,101,29,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(181,101,29,0.5) 1px, transparent 1px)", backgroundSize: "34px 34px" }} />
        <div className="relative max-w-3xl mx-auto px-5 sm:px-8">
          <motion.div {...fadeUp}>
            <span className="jh-sans text-xs uppercase tracking-[0.3em]" style={{ color: COPPER }}>Custom Gold &amp; Silver · Repair</span>
            <h1 className="mt-6 text-4xl sm:text-5xl font-bold" style={{ color: CREAM }}>Every piece starts as a spec sheet.</h1>
            <p className="mt-6 jh-sans text-lg" style={{ color: "#C7B79E" }}>Custom-made gold and silver pieces, careful repairs, and fair scrap-gold buying.</p>
            <div className="mt-9 flex flex-wrap justify-center gap-4 jh-sans">
              <a href="#custom" data-testid="jehannah-hero-cta" className="px-8 py-3.5 text-sm font-semibold" style={{ background: COPPER, color: CHARCOAL }}>Discuss a Custom Piece</a>
              <a href={BIZ.facebook} target="_blank" rel="noopener noreferrer" data-testid="jehannah-hero-message" className="px-8 py-3.5 text-sm font-semibold border" style={{ borderColor: "#C7B79E", color: CREAM }}>Message Us</a>
            </div>
            <p className="mt-8 jh-sans text-xs" style={{ color: "#8A7960" }}>{BIZ.areaNote}</p>
          </motion.div>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-5 sm:px-8 py-16 text-center">
        <motion.p {...fadeUp} className="jh-sans text-lg leading-relaxed" style={{ color: "#3A2E1C" }}>{BIZ.intro}</motion.p>
      </section>

      {/* CUSTOM — services */}
      <section id="custom" className="py-16" style={{ background: "#EEE3CC" }}>
        <div className="max-w-5xl mx-auto px-5 sm:px-8 grid sm:grid-cols-2 gap-6">
          {SERVICES.map((s) => (
            <motion.div key={s.title} {...fadeUp} className="p-6 bg-white shadow-sm jh-sans">
              <h3 className="font-semibold text-sm uppercase tracking-wide" style={{ color: COPPER }}>{s.title}</h3>
              <p className="mt-2 text-sm text-[#3A2E1C]/80">{s.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* SPECS — real gram-weight spec sheets, the core trust asset */}
      <section id="specs" className="max-w-6xl mx-auto px-5 sm:px-8 py-16">
        <motion.h2 {...fadeUp} className="text-3xl font-bold text-center mb-3">Real Spec Sheets, Real Orders</motion.h2>
        <motion.p {...fadeUp} className="jh-sans text-center text-[#5A4A32] mb-12 max-w-xl mx-auto">Every custom order gets a weight and stone spec, like these — nothing is guessed.</motion.p>
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6">
          {PRODUCTS.map((p) => (
            <motion.a key={p.name} {...fadeUp} href={BIZ.facebook} target="_blank" rel="noopener noreferrer" data-testid="jehannah-product-card" className="bg-white shadow-sm group">
              <div className="overflow-hidden"><img src={p.img} alt={p.name} className="w-full h-56 object-cover group-hover:scale-105 transition-transform duration-500" /></div>
              <div className="p-4 jh-sans">
                <h3 className="text-sm font-semibold">{p.name}</h3>
                <p className="mt-1 text-xs text-[#5A4A32]/75">{p.desc}</p>
              </div>
            </motion.a>
          ))}
        </div>
      </section>

      {/* REPAIRS + SCRAP GOLD */}
      <section id="repairs" className="py-16" style={{ background: CHARCOAL }}>
        <div className="max-w-4xl mx-auto px-5 sm:px-8 grid sm:grid-cols-2 gap-8 jh-sans">
          <motion.div {...fadeUp} className="p-7" style={{ background: "#2A241C" }}>
            <Wrench size={22} style={{ color: COPPER }} />
            <h3 className="mt-4 font-semibold" style={{ color: CREAM }}>Repairs &amp; Alterations</h3>
            <p className="mt-2 text-sm" style={{ color: "#C7B79E" }}>Resizing, restringing, re-tipping and general repair work.</p>
          </motion.div>
          <motion.div {...fadeUp} className="p-7" style={{ background: "#2A241C" }}>
            <Recycle size={22} style={{ color: COPPER }} />
            <h3 className="mt-4 font-semibold" style={{ color: CREAM }}>Scrap Gold &amp; Pawnshop Receipts</h3>
            <p className="mt-2 text-sm" style={{ color: "#C7B79E" }}>Buys old scrap gold and pawnshop receipts — fairly assessed.</p>
          </motion.div>
        </div>
      </section>

      {/* STEPS */}
      <section className="max-w-4xl mx-auto px-5 sm:px-8 py-16 grid sm:grid-cols-4 gap-6 text-center jh-sans">
        {STEPS.map((s) => (
          <motion.div key={s.n} {...fadeUp}>
            <div className="text-2xl font-bold" style={{ color: COPPER }}>{s.n}</div>
            <p className="mt-2 font-semibold text-sm">{s.title}</p>
            <p className="mt-1 text-xs text-[#5A4A32]/75">{s.text}</p>
          </motion.div>
        ))}
      </section>

      {/* VISIT */}
      <section id="visit" className="max-w-3xl mx-auto px-5 sm:px-8 py-16 text-center">
        <motion.h2 {...fadeUp} className="text-2xl font-bold mb-4">Message Us to Get Started</motion.h2>
        <motion.p {...fadeUp} className="jh-sans text-[#5A4A32] mb-6">{BIZ.areaNote}</motion.p>
        <motion.a {...fadeUp} href={BIZ.facebook} target="_blank" rel="noopener noreferrer" data-testid="jehannah-facebook-link" className="inline-flex items-center gap-2 jh-sans px-8 py-3.5 text-sm font-semibold" style={{ background: COPPER, color: CHARCOAL }}><Facebook size={16} /> Message on Facebook</motion.a>
      </section>

      <footer className="py-8 jh-sans" style={{ background: CHARCOAL, color: "#8A7960" }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8 flex flex-col sm:flex-row justify-between gap-3 text-xs">
          <p className="text-base font-bold" style={{ color: CREAM }}>Jehannah Goldsmith Jewelry Repair Shop</p>
          <p>This is an unsolicited concept website by MySiteCare. Not operated by or affiliated with {BIZ.name}. Confirm current storefront and pricing directly with the business.</p>
        </div>
      </footer>

      <a href={BIZ.facebook} target="_blank" rel="noopener noreferrer" data-testid="jehannah-sticky-contact" className="md:hidden fixed bottom-4 inset-x-4 z-50 jh-sans px-6 py-3.5 text-center text-sm font-semibold shadow-xl flex items-center justify-center gap-2" style={{ background: COPPER, color: CHARCOAL }}><MessageCircle size={17} /> Message Jehannah</a>
      <ConceptCTAPill slug="jehannah-goldsmith-concept" />
    </div>
  );
}
